import React, { useState } from 'react';
import styled from 'styled-components';
import SearchIcon from '@mui/icons-material/Search';

import { TextField, Autocomplete, Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getDocumentation } from '@requests/documentation';
import { DocsSections, DocsSubsections } from '../types';
import { AutocompletePopperPortal } from '@common/Autocomplete/Base';

const Container = styled.div<{ isModal?: boolean }>`
  padding: 0;
  width: ${(props) => (props.isModal ? '100%' : '425px')};
  background: ${({ theme }) => theme.background.standard};
  z-index: 10;

  .MuiOutlinedInput-root {
    ${(props) =>
      props.isModal &&
      `
      border: none;
      box-shadow: none;
      background: transparent;

      &:hover .MuiOutlinedInput-notchedOutline,
      &.Mui-focused .MuiOutlinedInput-notchedOutline,
      .MuiOutlinedInput-notchedOutline {
        border: none;
      }
    `}
  }
`;

const SearchResult = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ResultTitle = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.text.standard};
`;

const ResultPath = styled.div`
  font-size: 0.85em;
  color: ${({ theme }) => theme.text.subtext};
`;

const ResultPreview = styled.div`
  font-size: 0.9em;
  color: ${({ theme }) => theme.text.standard};
  opacity: 0.8;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 100%;
  white-space: normal;
  line-height: 1.4;
`;

interface SearchResult {
  id: string;
  title: string;
  path: string;
  preview: string;
  href: string;
  sectionName: string;
}

interface Props {
  isModal?: boolean;
  onClose?: () => void;
  autoFocus?: boolean;
}

export const DocsSearch: React.FC<Props> = ({ isModal, onClose, autoFocus }) => {
  const navigate = useNavigate();

  const [inputValue, setInputValue] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);

  const { data: documentation = [] } = useQuery({
    queryKey: ['docs'],
    queryFn: getDocumentation,
  });

  const getSearchResults = (input: string) => {
    const results: SearchResult[] = [];
    const searchTerm = input.toLowerCase();
    const searchWords = searchTerm.split(/\s+/);

    documentation.forEach((section: DocsSections) => {
      section.subsections.forEach((subsection: DocsSubsections) => {
        if (!subsection.document || subsection.dividor) return;

        // Check if all search words are present in the title
        const titleLower = subsection.name.toLowerCase();
        const matchInTitle = searchWords.every((word) => titleLower.includes(word));

        // Check if all search words are present in the content
        const contentLower = subsection.document.content.toLowerCase();
        const matchInContent = searchWords.every((word) => contentLower.includes(word));

        if (matchInTitle || matchInContent) {
          // Find the context around the search term in content
          let preview = '';
          if (matchInContent) {
            const content = subsection.document.content;
            // Find the first paragraph that contains any of the search words
            const paragraphs = content.split('\n\n');
            const matchingParagraph = paragraphs.find((p) =>
              searchWords.some((word) => p.toLowerCase().includes(word))
            );
            if (matchingParagraph) {
              preview = matchingParagraph.slice(0, 200) + '...';
            } else {
              // Fallback to showing context around the first match
              const index = content.toLowerCase().indexOf(searchTerm);
              const start = Math.max(0, index - 50);
              const end = Math.min(content.length, index + searchTerm.length + 50);
              preview = '...' + content.slice(start, end) + '...';
            }
          }

          const href = subsection.href || '';
          results.push({
            id: `${section.id}-${subsection.id}`,
            title: subsection.name,
            path: `${section.name} > ${subsection.name}`,
            preview: preview || subsection.document.content.slice(0, 100) + '...',
            href,
            sectionName: section.name,
          });
        }
      });
    });

    return results;
  };

  const handleResultClick = (result: SearchResult) => {
    if (result.href) {
      navigate(result.href);
      setSearchResults([]);
      if (isModal && onClose) onClose();
    }
  };

  const handleInputChange = (input: string) => {
    setInputValue(input);

    const results = getSearchResults(input);
    setSearchResults(results);
  };

  const getControlIcon = () => {
    const platform = navigator.userAgent.toLowerCase();

    if (platform.indexOf('mac') !== -1) return '⌘';
    return 'Ctrl';
  };

  return (
    <Container isModal={isModal}>
      <Autocomplete
        freeSolo
        options={searchResults}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.title)}
        inputValue={inputValue}
        onInputChange={(_, newValue) => handleInputChange(newValue)}
        onChange={(_, value) => {
          if (value && typeof value !== 'string') {
            handleResultClick(value);
          }
        }}
        autoHighlight
        PopperComponent={AutocompletePopperPortal}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder='Search documentation'
            variant='outlined'
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={autoFocus}
            InputProps={{
              ...params.InputProps,
              startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />,
              endAdornment: (
                <Typography variant='body2' color='text.secondary' mr={1}>
                  {`${getControlIcon()} + K`}
                </Typography>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box {...props} key={option.id} component='li' onClick={() => handleResultClick(option)}>
            <SearchResult>
              <ResultTitle>{option.title}</ResultTitle>
              <ResultPath>{option.path}</ResultPath>
              <ResultPreview>{option.preview}</ResultPreview>
            </SearchResult>
          </Box>
        )}
      />
    </Container>
  );
};
